import { Case, Client, Document, Status, Task, User } from '../types';
import { readStorage, writeStorage, createId } from './storage';
import {
  seedCases,
  seedClients,
  seedDocuments,
  seedTasks,
  seedUsers,
  seedCaseStatuses,
  seedTaskStatuses,
} from './seed';

const KEY = {
  cases: 'clicklaw.cases',
  clients: 'clicklaw.clients',
  documents: 'clicklaw.documents',
  tasks: 'clicklaw.tasks',
} as const;

export function initDb(): void {
  // Seed once for demo UX. If keys exist, never overwrite.
  if (!window.localStorage.getItem(KEY.cases)) writeStorage(KEY.cases, seedCases);
  if (!window.localStorage.getItem(KEY.clients)) writeStorage(KEY.clients, seedClients);
  if (!window.localStorage.getItem(KEY.documents)) writeStorage(KEY.documents, seedDocuments);
  if (!window.localStorage.getItem(KEY.tasks)) writeStorage(KEY.tasks, seedTasks);
}

export const lookups = {
  users: (): User[] => seedUsers,
  caseStatuses: (): Status[] => seedCaseStatuses,
  taskStatuses: (): Status[] => seedTaskStatuses,
};

export const caseRepo = {
  list(): Case[] {
    return readStorage<Case[]>(KEY.cases, []);
  },
  get(id: string): Case | undefined {
    return this.list().find((c) => c.id === id);
  },
  create(input: Omit<Case, 'id' | 'createdAt' | 'updatedAt'>): Case {
    const now = new Date().toISOString();
    const created: Case = { ...input, id: createId('case'), createdAt: now, updatedAt: now };
    const next = [created, ...this.list()];
    writeStorage(KEY.cases, next);
    return created;
  },
  update(id: string, patch: Partial<Omit<Case, 'id' | 'createdAt'>>): Case {
    const now = new Date().toISOString();
    const current = this.get(id);
    if (!current) throw new Error('Case not found');
    const updated: Case = { ...current, ...patch, updatedAt: now };
    const next = this.list().map((c) => (c.id === id ? updated : c));
    writeStorage(KEY.cases, next);
    return updated;
  },
  delete(id: string): void {
    writeStorage(KEY.cases, this.list().filter((c) => c.id !== id));
  },
};

export const clientRepo = {
  list(): Client[] {
    return readStorage<Client[]>(KEY.clients, []);
  },
  get(id: string): Client | undefined {
    return this.list().find((c) => c.id === id);
  },
  create(input: Omit<Client, 'id'>): Client {
    const created: Client = { ...input, id: createId('client') };
    const next = [created, ...this.list()];
    writeStorage(KEY.clients, next);
    return created;
  },
  update(id: string, patch: Partial<Omit<Client, 'id'>>): Client {
    const current = this.get(id);
    if (!current) throw new Error('Client not found');
    const updated: Client = { ...current, ...patch };
    const next = this.list().map((c) => (c.id === id ? updated : c));
    writeStorage(KEY.clients, next);
    return updated;
  },
  delete(id: string): void {
    writeStorage(KEY.clients, this.list().filter((c) => c.id !== id));
  },
};

export const documentRepo = {
  list(): Document[] {
    return readStorage<Document[]>(KEY.documents, []);
  },
  get(id: string): Document | undefined {
    return this.list().find((d) => d.id === id);
  },
  create(input: Omit<Document, 'id' | 'uploadedAt'>): Document {
    const now = new Date().toISOString();
    const created: Document = { ...input, id: createId('doc'), uploadedAt: now };
    const next = [created, ...this.list()];
    writeStorage(KEY.documents, next);
    return created;
  },
  update(id: string, patch: Partial<Omit<Document, 'id'>>): Document {
    const current = this.get(id);
    if (!current) throw new Error('Document not found');
    const updated: Document = { ...current, ...patch };
    const next = this.list().map((d) => (d.id === id ? updated : d));
    writeStorage(KEY.documents, next);
    return updated;
  },
  delete(id: string): void {
    writeStorage(KEY.documents, this.list().filter((d) => d.id !== id));
  },
};

export const taskRepo = {
  list(): Task[] {
    return readStorage<Task[]>(KEY.tasks, []);
  },
  get(id: string): Task | undefined {
    return this.list().find((t) => t.id === id);
  },
  create(input: Omit<Task, 'id'>): Task {
    const created: Task = { ...input, id: createId('task') };
    const next = [created, ...this.list()];
    writeStorage(KEY.tasks, next);
    return created;
  },
  update(id: string, patch: Partial<Omit<Task, 'id'>>): Task {
    const current = this.get(id);
    if (!current) throw new Error('Task not found');
    const updated: Task = { ...current, ...patch };
    const next = this.list().map((t) => (t.id === id ? updated : t));
    writeStorage(KEY.tasks, next);
    return updated;
  },
  delete(id: string): void {
    writeStorage(KEY.tasks, this.list().filter((t) => t.id !== id));
  },
};

